import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  type ElementRef,
  EventEmitter,
  HostListener,
  Input,
  type OnChanges,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import {
  eachDayOfInterval,
  endOfWeek,
  format,
  formatISO,
  getHours,
  getISOWeek,
  getMinutes,
  isSameDay,
  isToday,
  parseISO,
  startOfWeek,
} from 'date-fns';
import { de } from 'date-fns/locale';
import {
  buildTimeEntryStartTime,
  sumTimeEntryMinutes,
  timerTimeEntries,
  type FirestoreTimer,
  type FirestoreWork,
  type IsoDate,
  type IsoDateTime,
  type TimeEntry,
} from '@jessie/commons';
import { WeekWorkCardComponent } from './week-work-card.component';
import { WeekTimerCardComponent } from './week-timer-card.component';
import { CalendarStatusService } from '../calendar/calendar-status.service';
import {
  calendarStatusLaneClasses,
  type CalendarStatusMessage,
} from '../calendar/calendar-status-message';
import {
  clampMinutes,
  type MinuteRange,
  minuteRangeDuration,
  normalizeMinuteRange,
  rangeOverlapsAny,
  snapMinutesToIncrement,
} from './week-calendar-drag-range.helpers';

const GRID_START_HOUR = 6;
const GRID_END_HOUR = 20;
const PX_PER_HOUR = 60;

export type DayDensityBucket = 'empty' | 'low' | 'medium' | 'high';
export type WeekCalendarStatusSeverity = CalendarStatusMessage['severity'];
export type WeekCalendarStatusMessage = CalendarStatusMessage;

export type CalendarTimeEntryChange =
  | { kind: 'work'; work: FirestoreWork; sessionEntryId: string; nextEntry: TimeEntry }
  | {
      kind: 'paused-timer';
      timer: FirestoreTimer;
      sessionEntryId: string;
      nextEntry: TimeEntry;
    }
  | { kind: 'active-timer-start'; timer: FirestoreTimer; startTime: IsoDateTime };

interface WeekDayColumn {
  date: IsoDate;
  dateObj: Date;
  isToday: boolean;
  isSelected: boolean;
  hasWarning: boolean;
  dayLabel: string;
  allDayEntries: FirestoreWork[];
  timedEntries: PositionedEntry[];
  allDayTimers: FirestoreTimer[];
  timedTimerEntries: PositionedTimerEntry[];
  totalHours: number;
  densityBucket: DayDensityBucket;
}

interface PositionedEntry {
  id: string;
  work: FirestoreWork;
  sessionEntryId: string;
  topPx: number;
  heightPx: number;
  range: MinuteRange;
  sessionMinutes: number;
  isActualStart: boolean;
  isActualEnd: boolean;
  isSameDaySession: boolean;
}

interface PositionedTimerEntry {
  id: string;
  timer: FirestoreTimer;
  sessionEntryId: string;
  topPx: number;
  heightPx: number;
  range: MinuteRange;
  sessionMinutes: number;
  totalMinutes: number;
  projectDisplayName: string;
  positionDisplayName: string;
  isActualStart: boolean;
  isActualEnd: boolean;
  isSameDaySession: boolean;
}

type DragMode = 'create' | 'move' | 'resize-start' | 'resize-end';

type DragOwner =
  | { kind: 'work'; work: FirestoreWork; sessionEntryId: string }
  | { kind: 'paused-timer'; timer: FirestoreTimer; sessionEntryId: string }
  | { kind: 'active-timer-start'; timer: FirestoreTimer };

interface DragState {
  mode: DragMode;
  columnDate: IsoDate;
  pointerId: number;
  startClientX: number;
  startClientY: number;
  pointerStartMinutes: number;
  currentMinutes: number;
  originalRange: MinuteRange;
  owner: DragOwner | null;
  captureTarget: HTMLElement | null;
  hasMoved: boolean;
  invalidColumn: boolean;
}

interface GridHours {
  startHour: number;
  endHour: number;
}

interface TimeEntryPositionArgs {
  id: string;
  entryId: string;
  topPx: number;
  heightPx: number;
  range: MinuteRange;
  sessionMinutes: number;
  isActualStart: boolean;
  isActualEnd: boolean;
  isSameDaySession: boolean;
}

function roundDownToHour(minutes: number): number {
  return Math.floor(minutes / 60);
}

function roundUpToHour(minutes: number): number {
  return Math.ceil(minutes / 60);
}

function getTimeEntryBounds(timedEntries: TimeEntry[]): {
  startMinutes: number;
  endMinutes: number;
} | null {
  if (timedEntries.length === 0) return null;

  const initialBounds = {
    startMinutes: Number.POSITIVE_INFINITY,
    endMinutes: Number.NEGATIVE_INFINITY,
  };
  const bounds = timedEntries.reduce((current, entry) => {
    const startTime = parseISO(entry.startTime);
    if (isNaN(startTime.getTime())) return current;
    const startMinutes = getHours(startTime) * 60 + getMinutes(startTime);
    const endMinutes = startMinutes + entry.totalMinutes;
    return {
      startMinutes: Math.min(current.startMinutes, startMinutes),
      endMinutes: Math.max(current.endMinutes, endMinutes),
    };
  }, initialBounds);

  return bounds.startMinutes === Number.POSITIVE_INFINITY ? null : bounds;
}

function mergeTimeEntryBounds(
  left: { startMinutes: number; endMinutes: number } | null,
  right: { startMinutes: number; endMinutes: number } | null
): { startMinutes: number; endMinutes: number } | null {
  if (!left) return right;
  if (!right) return left;
  return {
    startMinutes: Math.min(left.startMinutes, right.startMinutes),
    endMinutes: Math.max(left.endMinutes, right.endMinutes),
  };
}

function runningTimerMinutes(timer: FirestoreTimer, now: Date): number {
  if (timer.data.timerState !== 'active') return 0;

  const start = parseISO(timer.data.startTime);
  if (isNaN(start.getTime())) return 0;

  return Math.max(1, Math.ceil((now.getTime() - start.getTime()) / 60_000));
}

export function timerTotalMinutes(timer: FirestoreTimer, now = new Date()): number {
  const savedMinutes =
    timer.data.workItemState === 'embedded' ? (timer.data.work.workMinutes ?? 0) : 0;
  return savedMinutes + runningTimerMinutes(timer, now);
}


function getPositionedEntryBounds(
  entries: Array<{ range: MinuteRange }>
): { startMinutes: number; endMinutes: number } | null {
  if (entries.length === 0) return null;

  const initialBounds = {
    startMinutes: Number.POSITIVE_INFINITY,
    endMinutes: Number.NEGATIVE_INFINITY,
  };
  const bounds = entries.reduce(
    (current, entry) => ({
      startMinutes: Math.min(current.startMinutes, entry.range.startMinutes),
      endMinutes: Math.max(current.endMinutes, entry.range.endMinutes),
    }),
    initialBounds
  );

  return bounds.startMinutes === Number.POSITIVE_INFINITY ? null : bounds;
}

function getVisibleEntryBounds<T extends { range: MinuteRange }>(
  dayIsos: IsoDate[],
  getEntriesForDay: (dayIso: IsoDate) => T[]
): { startMinutes: number; endMinutes: number } | null {
  return getPositionedEntryBounds(dayIsos.flatMap((dayIso) => getEntriesForDay(dayIso)));
}

/**
 * Resolve the visible grid range. Defaults to [GRID_START_HOUR, GRID_END_HOUR]
 * but expands the bounds when `workItems` contain timed entries that fall
 * outside the default window, so early-morning or late-evening work is
 * never clipped from view.
 */
export function resolveGridHours(
  workItems: FirestoreWork[],
  timerItems: FirestoreTimer[] = [],
  now = new Date(),
  dayIsos: IsoDate[] = []
): GridHours {
  const workTimeEntries = workItems.flatMap((work) => Object.values(work.data.timeEntries ?? {}));
  const timerTimeEntryValues = timerItems.flatMap((timer) =>
    Object.values(timerTimeEntries(timer.data, now))
  );
  const bounds = mergeTimeEntryBounds(
    dayIsos.length > 0
      ? getVisibleEntryBounds(dayIsos, (dayIso) =>
          workItems.flatMap((work) => toPositionedEntriesForDay(work, dayIso, 0, PX_PER_HOUR))
        )
      : getTimeEntryBounds(workTimeEntries),
    dayIsos.length > 0
      ? getVisibleEntryBounds(dayIsos, (dayIso) =>
          timerItems.flatMap((timer) =>
            toPositionedTimerEntriesForDay(timer, dayIso, 0, PX_PER_HOUR, now)
          )
        )
      : getTimeEntryBounds(timerTimeEntryValues)
  );
  if (!bounds) {
    return { startHour: GRID_START_HOUR, endHour: GRID_END_HOUR };
  }

  return {
    startHour: Math.min(GRID_START_HOUR, roundDownToHour(bounds.startMinutes)),
    endHour: Math.max(GRID_END_HOUR, roundUpToHour(bounds.endMinutes)),
  };
}

export function densityBucketFromHours(totalHours: number): DayDensityBucket {
  if (totalHours <= 0) return 'empty';
  if (totalHours <= 3) return 'low';
  if (totalHours <= 6) return 'medium';
  return 'high';
}

function toPositionedTimeEntriesForDay<TPositionedEntry>(
  ownerId: string,
  timeEntries: Record<string, TimeEntry> | null | undefined,
  dayIso: IsoDate,
  gridStartHour: number,
  pxPerHour: number,
  createPositionedEntry: (args: TimeEntryPositionArgs) => TPositionedEntry
): TPositionedEntry[] {
  const dayStart = parseISO(`${dayIso}T00:00:00`);
  const dayEnd = new Date(dayStart);
  dayEnd.setDate(dayEnd.getDate() + 1);

  return Object.entries(timeEntries ?? {})
    .sort(([, left], [, right]) => left.startTime.localeCompare(right.startTime))
    .flatMap(([entryId, entry]) => {
      if (!Number.isFinite(entry.totalMinutes) || entry.totalMinutes <= 0) return [];

      const entryStart = parseISO(entry.startTime);
      if (isNaN(entryStart.getTime())) return [];

      const entryEnd = new Date(entryStart.getTime() + entry.totalMinutes * 60_000);
      if (isNaN(entryEnd.getTime())) return [];

      const visibleStart = new Date(Math.max(entryStart.getTime(), dayStart.getTime()));
      const visibleEnd = new Date(Math.min(entryEnd.getTime(), dayEnd.getTime()));
      if (visibleEnd <= visibleStart) return [];

      const startMinutes = getHours(visibleStart) * 60 + getMinutes(visibleStart);
      const sessionMinutes = Math.round((visibleEnd.getTime() - visibleStart.getTime()) / 60_000);
      const range = { startMinutes, endMinutes: startMinutes + sessionMinutes };
      const sliceSuffix =
        dayIso === formatISO(entryStart, { representation: 'date' }) ? '' : `-${dayIso}`;

      return [
        createPositionedEntry({
          id: `${ownerId}-${entryId}${sliceSuffix}`,
          entryId,
          topPx: ((startMinutes - gridStartHour * 60) / 60) * pxPerHour,
          heightPx: Math.max((sessionMinutes / 60) * pxPerHour, 20),
          range,
          sessionMinutes,
          isActualStart: visibleStart.getTime() === entryStart.getTime(),
          isActualEnd: visibleEnd.getTime() === entryEnd.getTime(),
          isSameDaySession: isSameDay(entryStart, entryEnd),
        }),
      ];
    });
}

export function toPositionedEntriesForDay(
  work: FirestoreWork,
  dayIso: IsoDate,
  gridStartHour: number,
  pxPerHour: number
): PositionedEntry[] {
  return toPositionedTimeEntriesForDay(
    work.id,
    work.data.timeEntries,
    dayIso,
    gridStartHour,
    pxPerHour,
    ({
      id,
      entryId,
      topPx,
      heightPx,
      range,
      sessionMinutes,
      isActualStart,
      isActualEnd,
      isSameDaySession,
    }) => ({
      id,
      work,
      sessionEntryId: entryId,
      topPx,
      heightPx,
      range,
      sessionMinutes,
      isActualStart,
      isActualEnd,
      isSameDaySession,
    })
  );
}

function timerDisplay(timer: FirestoreTimer, now: Date) {
  const work = timer.data.workItemState === 'embedded' ? timer.data.work : null;
  return {
    totalMinutes: timerTotalMinutes(timer, now),
    projectDisplayName: work?.project?.displayName ?? 'Timer',
    positionDisplayName:
      work?.position?.displayName ??
      (timer.data.workItemState === 'linked' ? 'Verknüpfte Arbeit' : 'Ohne Position'),
  };
}

export function toPositionedTimerEntriesForDay(
  timer: FirestoreTimer,
  dayIso: IsoDate,
  gridStartHour: number,
  pxPerHour: number,
  now = new Date()
): PositionedTimerEntry[] {
  const dayStart = parseISO(`${dayIso}T00:00:00`);
  const display = timerDisplay(timer, now);

  return toPositionedTimeEntriesForDay(
    timer.id,
    timerTimeEntries(timer.data, now),
    formatISO(dayStart, { representation: 'date' }),
    gridStartHour,
    pxPerHour,
    ({
      id,
      entryId,
      topPx,
      heightPx,
      range,
      sessionMinutes,
      isActualStart,
      isActualEnd,
      isSameDaySession,
    }) => ({
      id,
      timer,
      sessionEntryId: entryId,
      topPx,
      heightPx,
      range,
      sessionMinutes,
      isActualStart,
      isActualEnd,
      isSameDaySession,
      ...display,
    })
  );
}
export function occupiedRangesForPositionedEntries(
  entries: Array<{ range: MinuteRange }>
): MinuteRange[] {
  return entries.map((entry) => entry.range);
}

function hasTimeEntries(timeEntries: Record<string, TimeEntry> | null | undefined): boolean {
  return Object.keys(timeEntries ?? {}).length > 0;
}

function residualWorkMinutes(work: FirestoreWork): number {
  return Math.max(0, work.data.workMinutes - sumTimeEntryMinutes(work.data.timeEntries));
}

function residualTimerMinutes(timer: FirestoreTimer, now: Date): number {
  return Math.max(
    0,
    timerTotalMinutes(timer, now) - sumTimeEntryMinutes(timerTimeEntries(timer.data, now))
  );
}

@Component({
  selector: 'app-week-calendar',
  templateUrl: './week-calendar.component.html',
  imports: [NgClass, WeekWorkCardComponent, WeekTimerCardComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WeekCalendarComponent implements OnChanges {
  constructor(@Optional() private readonly calendarStatus: CalendarStatusService | null = null) {}

  @Input({ required: true }) selectedDate!: IsoDate;
  @Input({ required: true }) workItems!: FirestoreWork[];
  @Input() timerItems: FirestoreTimer[] = [];
  @Input() warningDays: IsoDate[] = [];
  @Input() statusMessage: WeekCalendarStatusMessage | null = null;
  @Input() pendingTimeEntryChange: CalendarTimeEntryChange | null = null;

  @Output() selectedDateChange = new EventEmitter<IsoDate>();
  @Output() createWork = new EventEmitter<{
    date: IsoDate;
    workMinutes: number;
    startTime: string;
  }>();
  @Output() editWork = new EventEmitter<{ work: FirestoreWork; sessionEntryId?: string }>();
  @Output() showTimer = new EventEmitter<{ timerId: string; date: IsoDate }>();
  @Output() timeEntryChange = new EventEmitter<CalendarTimeEntryChange>();

  @ViewChild('timeGrid') timeGridRef!: ElementRef<HTMLDivElement>;

  weekTitle = '';
  weekTotalHours = 0;
  dayColumns: WeekDayColumn[] = [];
  timeSlots: string[] = [];
  dragState: DragState | null = null;
  overlapRejectionMessage: string | null = null;
  private calendarNow = new Date();
  private suppressNextEntryClick = false;
  readonly statusLaneClasses = calendarStatusLaneClasses;

  gridStartHour = GRID_START_HOUR;
  gridEndHour = GRID_END_HOUR;
  readonly pxPerHour = PX_PER_HOUR;

  get gridTotalHours(): number {
    return this.gridEndHour - this.gridStartHour;
  }

  ngOnChanges(): void {
    const now = new Date();
    const selected = parseISO(this.selectedDate);
    const weekStart = startOfWeek(selected, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(selected, { weekStartsOn: 1 });
    const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });
    const weekDayIsos = weekDays.map((day) => formatISO(day, { representation: 'date' }));
    if (
      this.dragState &&
      (this.pendingTimeEntryChange !== null || !weekDayIsos.includes(this.dragState.columnDate))
    ) {
      this.cancelDrag();
    }
    this.calendarNow = now;
    const { startHour, endHour } = resolveGridHours(
      this.workItems,
      this.timerItems,
      now,
      weekDayIsos
    );
    this.gridStartHour = startHour;
    this.gridEndHour = endHour;

    this.timeSlots = Array.from({ length: this.gridTotalHours }, (_, i) => {
      const h = this.gridStartHour + i;
      return `${h.toString().padStart(2, '0')}:00`;
    });

    const weekNumber = getISOWeek(weekStart);
    this.weekTitle = `KW ${weekNumber} · ${format(weekStart, 'd.', { locale: de })}–${format(weekEnd, 'd. MMMM yyyy', { locale: de })}`;

    this.dayColumns = weekDays.map((day) => {
      const dayIso = formatISO(day, { representation: 'date' });
      const dayWorks = this.workItems.filter((w) => w.data.date === dayIso);
      const dayTimers = this.timerItems.filter((timer) => timer.data.date === dayIso);
      const timedDayWorks = dayWorks.filter((w) => hasTimeEntries(w.data.timeEntries));
      const timedEntries = this.workItems
        .filter((w) => hasTimeEntries(w.data.timeEntries))
        .flatMap((w) => toPositionedEntriesForDay(w, dayIso, this.gridStartHour, PX_PER_HOUR))
        .sort((a, b) => a.topPx - b.topPx);
      const timedTimerEntries = this.timerItems
        .flatMap((timer) =>
          toPositionedTimerEntriesForDay(timer, dayIso, this.gridStartHour, PX_PER_HOUR, now)
        )
        .sort((a, b) => a.topPx - b.topPx);

      const allDayEntries = dayWorks.filter((w) => !hasTimeEntries(w.data.timeEntries));
      const timedDayTimers = dayTimers.filter((timer) =>
        hasTimeEntries(timerTimeEntries(timer.data, now))
      );
      const allDayTimers = dayTimers.filter(
        (timer) => !hasTimeEntries(timerTimeEntries(timer.data, now))
      );

      const workMinutes =
        allDayEntries.reduce((sum, work) => sum + work.data.workMinutes, 0) +
        timedEntries.reduce((sum, entry) => sum + entry.sessionMinutes, 0) +
        timedDayWorks.reduce((sum, work) => sum + residualWorkMinutes(work), 0);
      const timerMinutes =
        allDayTimers.reduce((sum, timer) => sum + timerTotalMinutes(timer, now), 0) +
        timedTimerEntries.reduce((sum, entry) => sum + entry.sessionMinutes, 0) +
        timedDayTimers.reduce((sum, timer) => sum + residualTimerMinutes(timer, now), 0);
      const totalHours = (workMinutes + timerMinutes) / 60;

      return {
        date: dayIso,
        dateObj: day,
        isToday: isToday(day),
        isSelected: isSameDay(day, selected),
        hasWarning: (this.warningDays ?? []).includes(dayIso),
        dayLabel: format(day, 'EEE d', { locale: de }),
        allDayEntries,
        timedEntries,
        allDayTimers,
        timedTimerEntries,
        totalHours,
        densityBucket: densityBucketFromHours(totalHours),
      };
    });

    this.weekTotalHours = this.dayColumns.reduce((sum, column) => sum + column.totalHours, 0);
  }

  formatWorkHours(hours: number): string {
    return `${hours.toFixed(1)}h`;
  }

  timerTotalMinutes(timer: FirestoreTimer): number {
    return timerTotalMinutes(timer, this.calendarNow);
  }

  get activeStatusMessage(): WeekCalendarStatusMessage | null {
    if (this.overlapRejectionMessage) {
      return { severity: 'warning', text: this.overlapRejectionMessage };
    }
    return this.statusMessage;
  }

  densitySurfaceClasses(column: WeekDayColumn, selectedClass = 'bg-primary-soft'): string {
    if (column.isSelected) return selectedClass;

    switch (column.densityBucket) {
      case 'low':
        return 'bg-work-load-xlight';
      case 'medium':
        return 'bg-work-load-light';
      case 'high':
        return 'bg-work-load-medium';
      default:
        return '';
    }
  }

  // ── Pointer gestures ───────────────────────────────────────

  onPointerDown(event: PointerEvent, column: WeekDayColumn): void {
    if (
      this.pendingTimeEntryChange ||
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      this.dragState ||
      (event.target as HTMLElement | null)?.closest('[data-week-entry]')
    ) {
      return;
    }
    this.selectedDateChange.emit(column.date);
    this.startDrag(event, column, 'create', null, {
      startMinutes: 0,
      endMinutes: 0,
    });
  }

  onWorkEntryPointerDown(
    event: PointerEvent,
    column: WeekDayColumn,
    entry: PositionedEntry
  ): void {
    if (!this.canMoveWorkEntry(entry)) return;
    this.startDrag(event, column, 'move', { kind: 'work', work: entry.work, sessionEntryId: entry.sessionEntryId }, entry.range);
  }

  onWorkEntryResizePointerDown(
    event: PointerEvent,
    column: WeekDayColumn,
    entry: PositionedEntry,
    mode: 'resize-start' | 'resize-end'
  ): void {
    if (!this.canResizeWorkEntry(entry, mode)) return;
    this.startDrag(event, column, mode, { kind: 'work', work: entry.work, sessionEntryId: entry.sessionEntryId }, entry.range);
  }

  onTimerEntryPointerDown(
    event: PointerEvent,
    column: WeekDayColumn,
    entry: PositionedTimerEntry
  ): void {
    if (!this.canMoveTimerEntry(entry)) return;
    this.startDrag(
      event,
      column,
      'move',
      { kind: 'paused-timer', timer: entry.timer, sessionEntryId: entry.sessionEntryId },
      entry.range
    );
  }

  onTimerEntryResizePointerDown(
    event: PointerEvent,
    column: WeekDayColumn,
    entry: PositionedTimerEntry,
    mode: 'resize-start' | 'resize-end'
  ): void {
    if (!this.canResizeTimerEntry(entry, column, mode)) return;
    const owner: DragOwner =
      entry.sessionEntryId === '__running'
        ? { kind: 'active-timer-start', timer: entry.timer }
        : { kind: 'paused-timer', timer: entry.timer, sessionEntryId: entry.sessionEntryId };
    this.startDrag(event, column, mode, owner, entry.range);
  }

  onWorkEntryClick(event: MouseEvent, column: WeekDayColumn, entry: PositionedEntry): void {
    if (this.consumeSuppressedEntryClick(event)) return;
    this.selectedDateChange.emit(column.date);
    this.editWork.emit({ work: entry.work, sessionEntryId: entry.sessionEntryId });
    event.stopPropagation();
  }

  onTimerEntryClick(
    event: MouseEvent,
    column: WeekDayColumn,
    entry: PositionedTimerEntry
  ): void {
    if (this.consumeSuppressedEntryClick(event)) return;
    this.selectedDateChange.emit(column.date);
    this.showTimer.emit({ timerId: entry.timer.id, date: column.date });
    event.stopPropagation();
  }

  private startDrag(
    event: PointerEvent,
    column: WeekDayColumn,
    mode: DragMode,
    owner: DragOwner | null,
    originalRange: MinuteRange
  ): void {
    if (
      this.pendingTimeEntryChange ||
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      this.dragState
    ) {
      return;
    }

    const minutes = this.eventToMinutes(event);
    if (minutes === null) return;

    if (mode === 'create') event.preventDefault();
    event.stopPropagation();
    this.setOverlapRejectionMessage(null);
    const captureTarget = this.timeGridRef?.nativeElement ?? null;
    if (captureTarget?.setPointerCapture) {
      captureTarget.setPointerCapture(event.pointerId);
    }
    this.dragState = {
      mode,
      columnDate: column.date,
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      pointerStartMinutes: minutes,
      currentMinutes: minutes,
      originalRange: mode === 'create' ? { startMinutes: minutes, endMinutes: minutes } : originalRange,
      owner,
      captureTarget,
      hasMoved: false,
      invalidColumn: false,
    };
  }

  @HostListener('document:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    const state = this.dragState;
    if (!state || event.pointerId !== state.pointerId) return;

    const columnDate = this.columnDateAtPointer(event);
    if (columnDate !== undefined && columnDate !== state.columnDate) {
      this.dragState = { ...state, invalidColumn: true };
      return;
    }

    const hasMoved =
      state.hasMoved ||
      Math.hypot(event.clientX - state.startClientX, event.clientY - state.startClientY) >= 4;
    if (!hasMoved) return;

    const minutes = this.eventToMinutes(event);
    if (minutes === null) return;
    this.dragState = {
      ...state,
      currentMinutes: minutes,
      hasMoved,
      invalidColumn: false,
    };
  }

  @HostListener('document:pointerup', ['$event'])
  onPointerUp(event?: PointerEvent): void {
    const state = this.dragState;
    if (!state || (event && event.pointerId !== state.pointerId)) return;

    const columnDate = event ? this.columnDateAtPointer(event) : undefined;
    const invalidColumn =
      state.invalidColumn || (columnDate !== undefined && columnDate !== state.columnDate);
    this.finishDrag({ ...state, invalidColumn });
  }

  @HostListener('document:pointercancel', ['$event'])
  onPointerCancel(event: PointerEvent): void {
    if (this.dragState?.pointerId === event.pointerId) this.cancelDrag();
  }

  onLostPointerCapture(event: PointerEvent): void {
    if (this.dragState?.pointerId === event.pointerId) this.cancelDrag();
  }

  private finishDrag(state: DragState): void {
    this.clearDragState();
    if (!state.hasMoved) return;

    if (state.mode !== 'create') {
      this.suppressNextEntryClick = true;
      setTimeout(() => {
        this.suppressNextEntryClick = false;
      });
    }
    if (this.pendingTimeEntryChange || state.invalidColumn) return;

    const range = this.candidateRangeForDrag(state);
    if (!range || range.endMinutes >= 24 * 60 || minuteRangeDuration(range) < 15) return;

    const column = this.dayColumns.find((dayColumn) => dayColumn.date === state.columnDate);
    if (
      !column ||
      rangeOverlapsAny(range, this.occupiedRangesForDrag(column, state.owner))
    ) {
      this.setOverlapRejectionMessage(
        'Der gewählte Zeitraum überschneidet sich mit einem bestehenden Eintrag.'
      );
      return;
    }

    this.setOverlapRejectionMessage(null);
    if (state.mode === 'create') {
      this.createWorkForRange(state.columnDate, range);
      return;
    }

    const nextEntry = {
      startTime: buildTimeEntryStartTime(state.columnDate, range.startMinutes),
      totalMinutes: minuteRangeDuration(range),
    };
    switch (state.owner?.kind) {
      case 'work':
        this.timeEntryChange.emit({
          kind: 'work',
          work: state.owner.work,
          sessionEntryId: state.owner.sessionEntryId,
          nextEntry,
        });
        return;
      case 'paused-timer':
        this.timeEntryChange.emit({
          kind: 'paused-timer',
          timer: state.owner.timer,
          sessionEntryId: state.owner.sessionEntryId,
          nextEntry,
        });
        return;
      case 'active-timer-start':
        this.timeEntryChange.emit({
          kind: 'active-timer-start',
          timer: state.owner.timer,
          startTime: nextEntry.startTime,
        });
    }
  }

  private cancelDrag(): void {
    this.clearDragState();
    this.setOverlapRejectionMessage(null);
  }

  private clearDragState(): void {
    const state = this.dragState;
    this.dragState = null;
    if (state?.captureTarget?.hasPointerCapture?.(state.pointerId)) {
      state.captureTarget.releasePointerCapture(state.pointerId);
    }
  }

  private candidateRangeForDrag(state: DragState): MinuteRange | null {
    const gridStartMinutes = this.gridStartHour * 60;
    const gridEndMinutes = this.gridEndHour * 60;
    const currentMinutes = clampMinutes(
      snapMinutesToIncrement(state.currentMinutes, 15),
      gridStartMinutes,
      gridEndMinutes
    );
    if (state.mode === 'create') {
      return normalizeMinuteRange(state.pointerStartMinutes, currentMinutes);
    }

    const duration = minuteRangeDuration(state.originalRange);
    if (
      duration > gridEndMinutes - gridStartMinutes ||
      (duration < 15 && state.owner?.kind !== 'active-timer-start')
    ) {
      return null;
    }

    switch (state.mode) {
      case 'move': {
        const startMinutes = clampMinutes(
          state.originalRange.startMinutes + currentMinutes - state.pointerStartMinutes,
          gridStartMinutes,
          gridEndMinutes - duration
        );
        return { startMinutes, endMinutes: startMinutes + duration };
      }
      case 'resize-start':
        if (state.originalRange.endMinutes - 15 < gridStartMinutes) return null;
        if (
          state.owner?.kind === 'active-timer-start' &&
          duration < 15 &&
          currentMinutes > state.originalRange.endMinutes - 15
        ) {
          return null;
        }
        return {
          startMinutes: clampMinutes(
            currentMinutes,
            gridStartMinutes,
            state.originalRange.endMinutes - 15
          ),
          endMinutes: state.originalRange.endMinutes,
        };
      case 'resize-end':
        if (state.originalRange.startMinutes + 15 > gridEndMinutes) return null;
        return {
          startMinutes: state.originalRange.startMinutes,
          endMinutes: clampMinutes(
            currentMinutes,
            state.originalRange.startMinutes + 15,
            gridEndMinutes
          ),
        };
      default:
        return null;
    }
  }

  private occupiedRangesForDrag(column: WeekDayColumn, owner: DragOwner | null): MinuteRange[] {
    const workEntries = column.timedEntries.filter(
      (entry) =>
        owner?.kind !== 'work' ||
        entry.work.id !== owner.work.id ||
        entry.sessionEntryId !== owner.sessionEntryId
    );
    const timerEntries = column.timedTimerEntries.filter(
      (entry) =>
        (owner?.kind !== 'paused-timer' ||
          entry.timer.id !== owner.timer.id ||
          entry.sessionEntryId !== owner.sessionEntryId) &&
        (owner?.kind !== 'active-timer-start' ||
          entry.timer.id !== owner.timer.id ||
          entry.sessionEntryId !== '__running')
    );
    return occupiedRangesForPositionedEntries([...workEntries, ...timerEntries]);
  }

  private columnDateAtPointer(event: PointerEvent): IsoDate | null | undefined {
    if (typeof document.elementFromPoint !== 'function') return undefined;
    const target = document.elementFromPoint(event.clientX, event.clientY);
    const column = target?.closest<HTMLElement>('[data-week-column-date]');
    return (column?.dataset.weekColumnDate as IsoDate | undefined) ?? null;
  }

  private consumeSuppressedEntryClick(event: MouseEvent): boolean {
    if (!this.suppressNextEntryClick) return false;
    this.suppressNextEntryClick = false;
    event.preventDefault();
    event.stopPropagation();
    return true;
  }


  canMoveWorkEntry(entry: PositionedEntry): boolean {
    return entry.isSameDaySession && entry.work.data.invoice == null;
  }

  canResizeWorkEntry(
    entry: PositionedEntry,
    mode: 'resize-start' | 'resize-end'
  ): boolean {
    return this.canMoveWorkEntry(entry) &&
      (mode === 'resize-start' ? entry.isActualStart : entry.isActualEnd);
  }

  canMoveTimerEntry(entry: PositionedTimerEntry): boolean {
    return (
      entry.timer.data.timerState === 'inactive' &&
      entry.timer.data.workItemState === 'embedded' &&
      this.isUuidSessionEntryId(entry.sessionEntryId) &&
      entry.isSameDaySession
    );
  }

  canResizeTimerEntry(
    entry: PositionedTimerEntry,
    column: WeekDayColumn,
    mode: 'resize-start' | 'resize-end'
  ): boolean {
    if (this.canMoveTimerEntry(entry)) {
      return mode === 'resize-start' ? entry.isActualStart : entry.isActualEnd;
    }
    return (
      mode === 'resize-start' &&
      entry.sessionEntryId === '__running' &&
      entry.timer.data.timerState === 'active' &&
      entry.timer.data.workItemState === 'embedded' &&
      entry.isSameDaySession &&
      entry.isActualStart &&
      entry.timer.data.date === column.date &&
      column.isToday
    );
  }

  private isUuidSessionEntryId(sessionEntryId: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      sessionEntryId
    );
  }

  onGridColumnKeydown(event: KeyboardEvent, column: WeekDayColumn): void {
    if (this.pendingTimeEntryChange) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const target = event.target as HTMLElement | null;
    const currentTarget = event.currentTarget as HTMLElement | null;
    if ((currentTarget && target !== currentTarget) || target?.closest('[data-week-entry]')) {
      return;
    }

    event.preventDefault();
    this.selectedDateChange.emit(column.date);
    this.setOverlapRejectionMessage(null);
    const startMinutes = this.gridStartHour * 60;
    const endMinutes = Math.min(startMinutes + 60, this.gridEndHour * 60);
    this.createWorkForRange(column.date, { startMinutes, endMinutes });
  }

  private createWorkForRange(columnDate: IsoDate, range: MinuteRange): void {
    const workMinutes = minuteRangeDuration(range);
    if (range.endMinutes >= 24 * 60 || workMinutes < 15) return;

    const column = this.dayColumns.find((dayColumn) => dayColumn.date === columnDate);
    if (
      column &&
      rangeOverlapsAny(
        range,
        occupiedRangesForPositionedEntries([...column.timedEntries, ...column.timedTimerEntries])
      )
    ) {
      this.setOverlapRejectionMessage(
        'Der gewählte Zeitraum überschneidet sich mit einem bestehenden Eintrag.'
      );
      return;
    }

    this.setOverlapRejectionMessage(null);
    this.createWork.emit({
      date: columnDate,
      workMinutes,
      startTime: buildTimeEntryStartTime(columnDate, range.startMinutes),
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.cancelDrag();
  }

  private setOverlapRejectionMessage(message: string | null): void {
    this.overlapRejectionMessage = message;
    if (message) {
      this.calendarStatus?.show({ severity: 'warning', text: message });
    }
  }

  private eventToMinutes(event: MouseEvent | PointerEvent): number | null {
    if (!this.timeGridRef) return null;
    const rect = this.timeGridRef.nativeElement.getBoundingClientRect();
    const offsetY = Math.min(Math.max(event.clientY - rect.top, 0), rect.height);

    const gridStartMin = this.gridStartHour * 60;
    const gridEndMin = this.gridEndHour * 60;
    const rawMinutes = (offsetY / PX_PER_HOUR) * 60 + gridStartMin;
    return clampMinutes(snapMinutesToIncrement(rawMinutes, 15), gridStartMin, gridEndMin);
  }

  dragOverlayForColumn(
    column: WeekDayColumn
  ): { topPx: number; heightPx: number; text: string } | null {
    const state = this.dragState;
    if (!state || state.columnDate !== column.date || state.invalidColumn) return null;
    const range = this.candidateRangeForDrag(state);
    return range ? this.rangeOverlay(range) : null;
  }

  pendingOverlayForColumn(
    column: WeekDayColumn
  ): { topPx: number; heightPx: number; text: string } | null {
    const pending = this.pendingTimeEntryChange;
    if (!pending) return null;

    if (pending.kind === 'active-timer-start') {
      const runningEntry = column.timedTimerEntries.find(
        (entry) => entry.timer.id === pending.timer.id && entry.sessionEntryId === '__running'
      );
      if (!runningEntry) return null;
      const start = parseISO(pending.startTime);
      if (isNaN(start.getTime())) return null;
      const startMinutes = getHours(start) * 60 + getMinutes(start);
      const endMinutes = runningEntry.range.endMinutes;
      const range = { startMinutes, endMinutes };
      return minuteRangeDuration(range) >= 15 ? this.rangeOverlay(range) : null;
    }

    const start = parseISO(pending.nextEntry.startTime);
    if (
      isNaN(start.getTime()) ||
      !Number.isFinite(pending.nextEntry.totalMinutes) ||
      pending.nextEntry.totalMinutes <= 0 ||
      formatISO(start, { representation: 'date' }) !== column.date
    ) {
      return null;
    }
    const range = {
      startMinutes: getHours(start) * 60 + getMinutes(start),
      endMinutes: getHours(start) * 60 + getMinutes(start) + pending.nextEntry.totalMinutes,
    };
    return this.rangeOverlay(range);
  }

  isPendingWorkEntry(entry: PositionedEntry): boolean {
    const pending = this.pendingTimeEntryChange;
    return (
      pending?.kind === 'work' &&
      pending.work.id === entry.work.id &&
      pending.sessionEntryId === entry.sessionEntryId
    );
  }

  isPendingTimerEntry(entry: PositionedTimerEntry): boolean {
    const pending = this.pendingTimeEntryChange;
    return (
      (pending?.kind === 'paused-timer' &&
        pending.timer.id === entry.timer.id &&
        pending.sessionEntryId === entry.sessionEntryId) ||
      (pending?.kind === 'active-timer-start' &&
        pending.timer.id === entry.timer.id &&
        entry.sessionEntryId === '__running')
    );
  }

  private rangeOverlay(range: MinuteRange): { topPx: number; heightPx: number; text: string } {
    const duration = minuteRangeDuration(range);
    const formatMinutes = (minutes: number) =>
      `${Math.floor(minutes / 60)
        .toString()
        .padStart(2, '0')}:${(minutes % 60).toString().padStart(2, '0')}`;
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    return {
      topPx: ((range.startMinutes - this.gridStartHour * 60) / 60) * PX_PER_HOUR,
      heightPx: Math.max((duration / 60) * PX_PER_HOUR, 2),
      text: `${formatMinutes(range.startMinutes)}–${formatMinutes(range.endMinutes)} · ${hours}h ${minutes}m`,
    };
  }
}
